import { useMemo, useState } from 'react';
import { set } from 'lockr';
import axios from 'axios';

import { useNavigate } from 'react-router-dom';
import { setSessionExpired, signOut } from 'pages/login/loginSlice';
import { getResponseErrorMessages } from 'utils/helpers';
import { useAppDispatch, useCurrentUser } from 'hooks';
import { msalInstance } from 'components/AuthWrapper';
import authorizationHeaders from 'common/authHeaders';
import { getToken, hasOtpSession } from 'utils/authService';
import ErrorModal from 'components/ErrorModal';
import { msalLogin, logOut } from 'utils/authService';
import { loginRequest } from 'config';
import api from 'api';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import path from 'common/path';

import { notify } from './notification/notificationSlice';

const messages = [
  'Azure ApplicationId Not Found',
  'User or Person already exists',
  'User has already been invited',
  // eslint-disable-next-line quotes
  "Provided Email doesn't exist", // VerifyEmail API
  'Access token cannot be null or empty.', // GetERPBankAccounts API
  'This account already exists and is active',
  'Range overlaps with an existing range for the BankAccountsId supplied', // AddUpdateBanksAccountsChecksAndRanges
  'Business owner must complete Microsoft Authentication flow to enable access to SMART-Hub and continue the payment workflow.', // VerifyCompliancePerson
  // OatFi onboarding
  'OatFi Onboarding retry count exceeded for BusinessId',
  'OatFi Onboarding already started for BusinessId',
  'Error getting',
  'Error creating',
];

const isTokenExpired = (errorResponse) => {
  return getToken() && errorResponse?.status === 401;
};

// the server ended the session (logout elsewhere, idle timeout, 8-hour limit, deactivation)
const isOtpSessionExpired = (errorResponse) => {
  return !getToken() && hasOtpSession() && errorResponse?.status === 401;
};

const isAccessNotAllowed = (errorResponse) => {
  return errorResponse?.status === 403;
};

const isValidationError = (errorResponse) => {
  return (
    errorResponse?.status === 400 &&
    Array.isArray(errorResponse?.data?.ErrorMessages) &&
    errorResponse.data.ErrorMessages.length > 0
  );
};

const isServerError = (errorResponse) => {
  if (!errorResponse) {
    set('serverTimeout', true);
    return true;
  }

  const errorMessage = getResponseErrorMessages(errorResponse, true);
  if (messages.some((message) => errorMessage?.includes(message))) {
    return false;
  }

  return errorResponse?.status === 500;
};

const ApiInterceptors = () => {
  const { user, logoutRedirect } = useCurrentUser();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [serverError, setServerError] = useState(false);

  //eslint-disable-next-line @typescript-eslint/no-unused-vars
  const simulateInteractionRequired = () => {
    // You can customize the error message and code as needed for your test case
    const errorCode = 'interaction_required';
    const errorMessage = 'A manual interaction is required for this action.';

    throw new InteractionRequiredAuthError(errorCode, errorMessage);
  };

  const handleLogout = async () => {
    dispatch(signOut());
    logoutRedirect();
    logOut();
    dispatch(setSessionExpired(true));
    navigate(path.login);
  };

  const resetToken = async () => {
    try {
      const result = await msalInstance.acquireTokenSilent({
        account: user,
        ...loginRequest,
      });

      msalLogin(result?.accessToken);
      return result?.accessToken;
    } catch (error: any) {
      if (error instanceof InteractionRequiredAuthError) {
        handleLogout();
      }
      throw error;
    }
  };

  useMemo(() => {
    api.interceptors.request.use((config) => {
      const accessToken = getToken();
      if (accessToken && config?.headers) {
        config.headers.Authorization = authorizationHeaders.bearer(accessToken);
      }

      // OTP users are identified by the HttpOnly session cookie (withCredentials), never by a header
      return config;
    });

    api.interceptors.response.use(
      (response) => {
        if (response?.config?.returnResponseBody) {
          return response.data;
        }

        if (response?.status && response?.data) {
          return response.data.hasOwnProperty('result')
            ? response.data.result
            : response.data;
        }
        return response;
      },
      async (error) => {
        const originalRequest = error.config;
        const { response: errorResponse } = error;

        // Handle token expiration with max 2 retries
        if (isTokenExpired(errorResponse)) {
          originalRequest._retryCount = originalRequest._retryCount || 0;

          if (originalRequest._retryCount >= 2) {
            handleLogout();
            return Promise.reject(error);
          }

          originalRequest._retryCount += 1;

          try {
            originalRequest._retry = true;

            const token = await resetToken();

            axios.defaults.headers.common.Authorization =
              authorizationHeaders.bearer(token);
            originalRequest.headers.Authorization =
              authorizationHeaders.bearer(token);

            return await api(originalRequest);
          } catch (err) {
            return Promise.reject(err);
          }
        }

        if (isOtpSessionExpired(errorResponse)) {
          // same as the Microsoft path: logoutRedirect never navigates (onRedirectNavigate: false) but its MSAL
          // logout events remount the app, which signOut() alone leaves stuck on the theme loader
          handleLogout();
          return Promise.reject(error);
        }

        if (isAccessNotAllowed(errorResponse)) {
          dispatch(
            notify({
              message: 'common:permissions.apiForbiddenError',
              variant: 'error',
              duration: 5000,
            }),
          );
        }

        if (
          isServerError(errorResponse) &&
          originalRequest.skipServerErrorModal !== true
        ) {
          setServerError(true);
        }

        if (isValidationError(errorResponse)) {
          dispatch(
            notify({
              message: getResponseErrorMessages(errorResponse),
              variant: 'error',
              duration: 10000,
            }),
          );
        }

        return Promise.reject(error);
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ErrorModal serverError={serverError} setServerError={setServerError} />
  );
};

export default ApiInterceptors;
